import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Sparkles, Calendar, Mail, CheckCircle2 } from 'lucide-react';
import { useDiscovery } from './DiscoveryContext';
import './chat.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const Bubble = ({ children, role }) => (
  <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        role === 'user'
          ? 'bg-zinc-950 text-white rounded-br-md'
          : 'bg-zinc-100 text-zinc-900 rounded-bl-md'
      }`}
    >
      {children}
    </div>
  </div>
);

export const ChatWidget = () => {
  const { openBookCall } = useDiscovery();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [bookingPrompt, setBookingPrompt] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(null);
  const emailCapturedRef = useRef(null);
  const [scoreOnTheWay, setScoreOnTheWay] = useState(false);
  const scrollRef = useRef(null);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const res = await fetch(`${API_URL}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: window.location.pathname }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: data.message }]);
      return data.session_id;
    } catch {
      return null;
    }
  }, [sessionId]);

  // Open handler — lazily creates the session
  const onOpen = async () => {
    setOpen(true);
    if (!sessionId && messages.length === 0) {
      await ensureSession();
    }
  };

  // Auto-scroll to last message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setBookingPrompt(false);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    // Auto-detect any email the visitor types — fire /identify so the Growth
    // Score email goes out as soon as they share it.
    const emailMatch = text.match(EMAIL_REGEX);
    let sidForIdentify = sessionId;
    try {
      let sid = sessionId;
      if (!sid) sid = await ensureSession();
      if (!sid) throw new Error('session');
      sidForIdentify = sid;
      const res = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, message: text }),
      });
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
      if (data.wants_booking) setBookingPrompt(true);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "I had a quick wobble — try again, or call 01603 976281 directly." },
      ]);
    } finally {
      setSending(false);
    }

    if (emailMatch && !emailCapturedRef.current && sidForIdentify) {
      const captured = emailMatch[0].toLowerCase();
      emailCapturedRef.current = captured;
      setEmailCaptured(captured);
      try {
        const r = await fetch(`${API_URL}/api/chat/identify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sidForIdentify, email: captured }),
        });
        const j = await r.json();
        if (j.growth_score_queued) setScoreOnTheWay(true);
      } catch {
        /* non-blocking */
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openCal = () => {
    setOpen(false);
    openBookCall();
  };

  return (
    <div className="bl-chat-bubble" data-testid="chat-widget-root">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[72px] right-0 w-[min(94vw,380px)] h-[min(72vh,560px)] bg-white rounded-3xl flex flex-col overflow-hidden border border-zinc-200 bl-chat-panel-shadow"
            data-testid="chat-widget-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 bg-gradient-to-br from-zinc-950 to-zinc-800 text-white relative">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shrink-0 shadow-md">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm tracking-tight">BrightLabs assistant</p>
                  <p className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    24/7 · Replies in seconds
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-zinc-400 hover:text-white p-1 -mr-1"
                  data-testid="chat-widget-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-50/30" data-testid="chat-widget-messages">
              {messages.length === 0 && (
                <div className="flex items-center gap-2 text-zinc-400 text-xs">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role}>{m.content}</Bubble>
              ))}
              {sending && (
                <Bubble role="assistant">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                </Bubble>
              )}
              {bookingPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-violet-50 px-4 py-3.5"
                  data-testid="chat-booking-prompt"
                >
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Calendar size={11} />Want to book a call?
                  </p>
                  <button
                    onClick={openCal}
                    className="w-full bg-zinc-950 text-white hover:bg-zinc-800 px-4 py-2.5 rounded-full font-bold text-xs flex items-center justify-center gap-2 transition-colors"
                    data-testid="chat-open-calendar"
                  >
                    <Calendar size={13} />Pick a time
                  </button>
                </motion.div>
              )}
              {scoreOnTheWay && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 px-4 py-3.5"
                  data-testid="chat-score-prompt"
                >
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <CheckCircle2 size={11} />Growth Score on its way
                  </p>
                  <p className="text-xs text-emerald-900/80 font-medium flex items-center gap-1.5">
                    <Mail size={10} />Heading to <strong>{emailCaptured}</strong> in seconds.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-zinc-100 bg-white p-3 flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask anything — pricing, fit, packages…"
                rows={1}
                data-testid="chat-input"
                className="flex-1 resize-none bg-zinc-100 focus:bg-white border border-transparent focus:border-zinc-300 rounded-2xl px-4 py-2.5 text-sm text-zinc-950 placeholder:text-zinc-400 outline-none transition-colors max-h-32"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                className="shrink-0 w-10 h-10 rounded-full bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                data-testid="chat-send"
                aria-label="Send"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
            <p className="text-[10px] text-zinc-400 text-center pb-2 px-3">AI assistant · Or call us on 01603 976281</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating bubble */}
      <motion.button
        onClick={open ? () => setOpen(false) : onOpen}
        data-testid="chat-widget-toggle"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_12px_28px_rgba(9,9,11,0.25)] transition-colors ${
          open ? 'bg-zinc-700' : 'bg-zinc-950 hover:bg-zinc-800'
        }`}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X size={22} />
            </motion.span>
          ) : (
            <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <MessageCircle size={22} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Unread nudge */}
      {!open && messages.length === 0 && (
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 3, duration: 0.4 }}
          className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white"
        />
      )}
    </div>
  );
};
